// Small wrapper around fetch() that pulls the CSRF token from the
// <meta name="csrf-token"> element Rails injects on every page.
// Use for JSON endpoints that aren't Inertia-shaped (e.g. master data
// mutations, where the controller returns JSON and we manually trigger
// router.reload on the frontend).
function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  )
}

// Build request headers with the CSRF token enforced. Returns a Headers
// instance so Headers / tuple-array / object forms of `extra` are all
// preserved, and sets X-CSRF-Token LAST so a caller can't override it.
// Content-Type defaults to JSON but stays caller-overridable.
export function csrfHeaders(extra: HeadersInit = {}): Headers {
  const headers = new Headers(extra)
  if (!headers.has("Content-Type"))
    headers.set("Content-Type", "application/json")
  headers.set("X-CSRF-Token", csrfToken())
  return headers
}

export async function csrfFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: csrfHeaders(options.headers || {}),
  })
}

// Like csrfFetch but for multipart/FormData bodies: sends only the CSRF token
// and lets the browser set Content-Type (incl. the multipart boundary).
// csrfHeaders forces Content-Type: application/json, which breaks file uploads.
export async function csrfFormFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  // Build from options.headers so Headers / tuple / object forms survive, then
  // set the token LAST so it can't be overridden. No Content-Type — the browser
  // sets it (incl. the multipart boundary) for FormData bodies.
  const headers = new Headers(options.headers)
  headers.set("X-CSRF-Token", csrfToken())
  return fetch(url, { ...options, headers })
}
